import React from "react";
import ReactDOM from "react-dom/client";

import { msalInstance } from "./auth/msalConfig";
import { setSelectedProvider } from "./auth/authProviders";
import { applyThemeEarly } from "./theme/useDarkMode";
import App from "./App";
import "@astrana/ui/styles.css";
import "./index.css";

async function bootstrap() {
  // Apply the persisted theme before anything paints so a dark-preference
  // user doesn't see a light-mode flash while React hydrates.
  applyThemeEarly();

  // Handle the MSAL redirect response before React mounts. This is
  // load-bearing for the Entra login flow: sign-in uses loginRedirect (see
  // auth/AuthContext.tsx), so the user returns from Azure AD to this page with
  // the auth response in the URL hash. Consuming it here — before React and the
  // router mount — is what prevents hash_empty_error, since the router would
  // otherwise strip the hash before MSAL reads it. On a successful response we
  // record the active provider so AuthContext acquires a token for the returned
  // account and treats the user as signed in.
  //
  // MSAL also persists its `interaction.status` flag in localStorage (because
  // cacheLocation is "localStorage" — see auth/msalConfig.ts). If a prior
  // interactive attempt was interrupted (tab closed mid-flow, network hiccup)
  // the flag can survive across page loads and make the next
  // handleRedirectPromise / loginRedirect throw hash_empty_error /
  // no_token_request_cache_error / interaction_in_progress with no real way to
  // recover except clearing it. Treat those three codes as stranded state and
  // wipe the cache so the next sign-in starts clean.
  await msalInstance.initialize();
  try {
    const result = await msalInstance.handleRedirectPromise();
    if (result?.account) {
      setSelectedProvider("entra");
    }
  } catch (err) {
    const code = (err as { errorCode?: string })?.errorCode;
    if (
      code === "hash_empty_error" ||
      code === "no_token_request_cache_error" ||
      code === "interaction_in_progress"
    ) {
      await msalInstance.clearCache();
    } else {
      console.error(err);
    }
  }

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

bootstrap();
